/** @jsxImportSource @emotion/react */
import { css, Theme } from '@emotion/react';
import React, {
	FC,
	Fragment,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { FaFacebookF, FaInstagram } from 'react-icons/fa';
import styled from '@emotion/styled/macro';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Flex, Box, Grid } from 'components/styled';
import Text from 'components/styled/Text';
import TitleText from 'components/styled/TitleText';
import {
	Wrapper,
	ResponsiveWrapper,
	ResponsivePadder,
} from 'components/styled/Wrapper';
import { NavLinkButton as NavLinkButtonOrg } from 'components/styled/Button';
import ScrollToTopButton from 'components/floatingactions/ScrollToTopButton';
import Link, { HrefLink } from 'components/styled/Link';
import TestimonialCarousel from 'components/homepage/TestimonialCarousel';
import ContactCard, { TContactCard } from 'components/homepage/ContactCard';
import HomepageCard, { THomepageCard } from 'components/homepage/HomepageCard';
import NewestOffers from 'components/homepage/NewestOffers';
import {
	CareerCounseling,
	Workshops,
	Mentoring,
	Internships,
	Jobfair,
	Coaching,
	Diagnostics,
	Experience,
	// News,
	Events,
	Collaboration,
	Advertising,
	Ambassadors,
} from 'components/homepage/ModalCards';

import { useLoggedInUser } from 'api';

import { ReactComponent as PhoneIcon } from 'assets/zlin/phone.svg';
import { ReactComponent as MailIcon } from 'assets/zlin/mail.svg';
import { ReactComponent as HandshakeIcon } from 'assets/zlin/handshake.svg';
import { ReactComponent as MegaphoneIcon } from 'assets/zlin/megaphone.svg';
import { ReactComponent as EventIcon } from 'assets/zlin/event.svg';
import { ReactComponent as ClockIcon } from 'assets/zlin/clock.svg';

import { usePageTitle } from 'hooks/seoHooks';

import { useLocalizedMultistring } from 'utils/localization';

import { useScrollToSection } from '../_utils';

import { ReactComponent as StudentsHorizontal } from './student.svg';
import { ReactComponent as AboutUsHorizontal } from './aboutus.svg';
import { ReactComponent as AboutUsEnHorizontal } from './aboutus_en.svg';
import { ReactComponent as CompanyHorizontal } from './company.svg';
import { ReactComponent as CompanyEnHorizontal } from './company_en.svg';
import { ReactComponent as ContactHorizontal } from './contact.svg';
import { ReactComponent as ContactEnHorizontal } from './contact_en.svg';
import LogoUtb from './utb-logo.svg';
import studentsHomeBg from './homeBg.jpg';
import studentshome from './studentshome.jpg';
import unicomplex from './unicomplex.jpg';
import companyhome from './companyhome.jpg';
import mapPlaceholder from './jchmap.png';
import contact1 from './contact_hana_zahorovska.jpg';
import contact2 from './contact_marek_prokop.jpg';
import contact3 from './contact_ostrihonova.jpg';
import contact5 from './contact_majchrakova.jpg';

const contactPersons: TContactCard[] = [
	{
		photo: contact1,
		position: [
			{ CZECH: 'vedoucí Job Centra', ENGLISH: 'head of the Job Center' },
			{
				CZECH: 'firemní spolupráce s UTB',
				ENGLISH: 'cooperation with companies',
			},
		],
		name: 'Ing. Hana Záhorovská',
		email: 'zahorovska@utb.cz',
		phone: '+420 576 032 224',
	},
	{
		photo: contact2,
		position: [
			{
				CZECH: 'kariérní poradenství pro studenty/absolventy',
				ENGLISH: 'career counselor for students / alumni',
			},
		],
		name: 'Mgr. Marek Prokop',
		email: 'mprokop@utb.cz',
		phone: '+420 576 032 021',
	},
	{
		photo: contact3,
		position: [
			{
				CZECH: 'registrace a inzerce pro firmy na Job Portale',
				ENGLISH: 'registration and advertising for companies',
			},
			{
				CZECH: 'zákaznický servis pro firmy na Job Portalu',
				ENGLISH: 'customer service for companies on the Job Portal',
			},
		],
		name: 'Ivana Ostrihoňová',
		email: 'zamestnavatel@utb.cz',
		phone: '+420 731 137 873',
	},
	{
		photo: contact5,
		position: [
			{ CZECH: 'MKT a propagace', ENGLISH: 'Marketing and PR' },
			{
				CZECH: 'Mentorink, Klub absolventů UTB',
				ENGLISH: 'mentoring, Alumni Club TBU',
			},
		],
		name: 'Radka Majchráková',
	},
];

export const TitleTextCustom = styled(TitleText)`
	text-transform: uppercase;
	text-align: left;
	padding: 0px !important;
	margin: 0px !important;
`;

const NavLinkButton = styled(NavLinkButtonOrg)`
	text-transform: uppercase;
	font-weight: bold;
	border: 1px solid transparent;
	&:hover {
		border: 1px solid ${p => p.theme.colors.primary};
		color: ${p => p.theme.colors.primary};
		background-color: white;
		text-decoration: none;
	}
`;

const ZlinHomepage: FC = () => {
	const localize = useLocalizedMultistring();
	const { i18n, t } = useTranslation('homepage/cards_headers/');

	const homepageEvents: THomepageCard[] = useMemo(
		() => [
			{
				Icon: EventIcon,
				title: t('events_title'),
				desc: t('events_description'),
				to: '/eventy/',
				card: Events,
			},
			{
				Icon: HandshakeIcon,
				title: t('collaboration_title'),
				desc: t('collaboration_description'),
				to: '/partnerstvi/',
				card: Collaboration,
			},
			{
				Icon: MegaphoneIcon,
				title: t('advertising_title'),
				desc: t('advertising_description'),
				to: '/inzerce/',
				card: Advertising,
			},
		],
		[t],
	);
	const homepageOffers: THomepageCard[] = useMemo(
		() => [
			{
				title: t('careercounseling_title'),
				desc: t('careercounseling_description'),
				to: '/karierni-poradenstvi/',
				card: CareerCounseling,
			},
			{
				title: t('workshops_title'),
				desc: t('workshops_description'),
				to: '/workshopy/',
				card: Workshops,
			},
			{
				title: t('mentoring_title'),
				desc: t('mentoring_description'),

				to: '/mentoring/',
				card: Mentoring,
			},
			{
				title: t('interships_title'),
				desc: t('interships_description'),
				to: '/staze/',
				card: Internships,
			},
			{
				title: t('jobfair_title'),
				desc: t('jobfair_description'),
				to: '/veletrh/',
				card: Jobfair,
			},
			{
				title: t('coaching_title'),
				desc: t('coaching_description'),
				to: '/koucink/',
				card: Coaching,
			},
			{
				title: t('diagnostics_title'),
				desc: t('diagnostics_description'),
				to: '/profesni-diagnostika/',
				card: Diagnostics,
			},
			{
				title: t('experience_title'),
				desc: t('experience_description'),
				to: '/odborna-praxe-na-utb/',
				card: Experience,
			},
			// {
			// 	title: t('news_title'),
			// 	desc: t('news_description'),
			// 	to: '/ostatni-aktuality-novinky/',
			// 	card: News,
			// },
			{
				title: t('ambassadors_title'),
				desc: t('ambassadors_description'),
				to: '/ambasadori/',
				card: Ambassadors,
			},
		],
		[t],
	);

	// Page title
	usePageTitle(localize({ CZECH: 'Vítejte', ENGLISH: 'Welcome' }));

	const user = useLoggedInUser();

	const aboutSectionRef = useRef<HTMLDivElement>(null);
	const companySectionRef = useRef<HTMLDivElement>(null);
	const studentSectionRef = useRef<HTMLDivElement>(null);
	const eventsSectionRef = useRef<HTMLDivElement>(null);
	const contactSectionRef = useRef<HTMLDivElement>(null);

	const { anchor } = useParams<{ anchor: string }>();

	let section: React.RefObject<HTMLDivElement> | null = null;
	switch (anchor) {
		case 'about':
			section = aboutSectionRef;
			break;
		case 'company':
			section = companySectionRef;
			break;
		case 'student':
			section = studentSectionRef;
			break;
		case 'events':
			section = eventsSectionRef;
			break;
		case 'contact':
			section = contactSectionRef;
			break;
	}

	const [offsetHeight, setOffsetHeight] = useState(0);
	useLayoutEffect(
		() =>
			setOffsetHeight(
				user ? 0 : document.getElementById('page-header')?.offsetHeight ?? 0,
			),
		[user],
	);
	useScrollToSection(section, offsetHeight);

	return (
		<Wrapper>
			<Flex
				css={css`
					background-image: url(${studentsHomeBg});
					background-size: cover;
					background-position: center center;
					background-repeat: no-repeat;
					width: auto;
					height: auto;
					min-height: 474px;
					max-height: 474px;
				`}
			>
				<ResponsiveWrapper pt={user ? [5, 6] : [3, 5]}>
					<TitleTextCustom
						as="h1"
						color="white"
						maxWidth={400}
						fontSize="xl"
						css={css`
							text-shadow: 0px 4px 7px rgba(0, 0, 0, 0.4);
						`}
					>
						{localize({
							CZECH:
								'Váháte, kam směřovat svoji kariéru nebo hledáte nové nadané zaměstnance?',
							ENGLISH:
								'Are you looking for career ideas or do you seek new talented employees?',
						})}
					</TitleTextCustom>

					{user ? null : (
						<Flex flexDirection={['column', 'row']} mt={3}>
							<NavLinkButton variant="primary" px={4} to="/login">
								{localize({
									CZECH: 'Student / Absolvent',
									ENGLISH: 'Student / Alumnus',
								})}
							</NavLinkButton>
							<NavLinkButton
								variant="primary"
								ml={[0, 3]}
								mt={[3, 0]}
								px={4}
								to="/login/company"
							>
								{localize({ CZECH: 'Zaměstnavatel', ENGLISH: 'Company' })}
							</NavLinkButton>
						</Flex>
					)}
					<Flex mt={3} alignItems="center" position="relative" left="-18px">
						<img
							src={LogoUtb}
							alt={localize({
								CZECH: 'Logo univerzity',
								ENGLISH: 'University logo',
							})}
							height={60}
						/>
					</Flex>
				</ResponsiveWrapper>
			</Flex>

			{/* POSLEDNI INZERATY  */}
			<NewestOffers />

			<Wrapper py={[4, 3]} bg="primary" color="white">
				<ResponsivePadder
					justifyContent={['center', 'space-around']}
					width={1}
					alignItems="center"
					flexDirection={['column', 'row']}
				>
					<Box as="span">
						<PhoneIcon fill="white" />
						<HrefLink color="white" ml={3} href="tel:+420731137873">
							+420 731 137 873
						</HrefLink>
					</Box>
					<Box as="span" mt={[4, 0]}>
						<MailIcon fill="white" />
						<HrefLink color="white" ml={3} href="mailto:jobcentrum@utb.cz">
							jobcentrum@utb.cz
						</HrefLink>
					</Box>

					<Box as="span" mt={[4, 0]}>
						<ClockIcon fill="white" />
						<Text as="span" color="white" ml={3}>
							{localize({ CZECH: 'Po', ENGLISH: 'Mon' })}
							&thinsp;-&thinsp;{localize({ CZECH: 'Pá', ENGLISH: 'Fri' })}
							&ensp;9:00&thinsp;-&thinsp;15:00
						</Text>
					</Box>
				</ResponsivePadder>
			</Wrapper>

			<ResponsivePadder
				py={5}
				my={3}
				alignItems="center"
				css={(theme: Theme) => css`
					color: black;

					@media (min-width: ${theme.breakpoints[3]}) {
						grid-template-columns: minmax(0, 1fr) minmax(0, 4fr);
						gap: ${theme.space[5]}px;
						display: grid;
					}
				`}
				ref={aboutSectionRef}
			>
				<Box gridColumn={1} gridRow={1}>
					<Box
						height={[100, null, null, 150]}
						left={
							i18n.language === 'en'
								? [88, null, null, 68, 0]
								: [40, null, null, 20, 0]
						}
						css={(theme: Theme) => css`
							position: relative;
							top: 0px;
							transform: scale(${i18n.language === 'en' ? 0.4 : 0.5})
								rotate(90deg) translateX(-50%) translateY(-50%);

							@media (min-width: ${theme.breakpoints[3]}) {
								// set transform to 'none' and top to '-130px' when video element is added again
								transform: scale(${i18n.language === 'en' ? 0.7 : 1});
								top: ${i18n.language === 'en' ? '-160px' : '-130px'};
							}
						`}
					>
						{i18n.language === 'en' ? (
							<AboutUsEnHorizontal />
						) : (
							<AboutUsHorizontal />
						)}
					</Box>
				</Box>
				<Flex
					width={1}
					flexDirection={['column', 'row']}
					css={(theme: Theme) => css`
						@media (min-width: 640px) {
							gap: ${theme.space[3]}px;
						}
					`}
				>
					<Box width={[1, 1 / 2]}>
						<TitleTextCustom color="primary">
							{localize({
								CZECH: 'Job Centrum a jeho služby',
								ENGLISH: 'Job Center and its services',
							})}
						</TitleTextCustom>
						<Text mt={3} mb={4} color="secondary">
							{localize({
								CZECH:
									'Od roku 2012 hrdě propojujeme studenty se zaměstnavateli.',
								ENGLISH:
									'We have been proudly interconnecting students and employers since 2012.',
							})}{' '}
						</Text>
						<TitleTextCustom color="primary">
							{localize({ CZECH: 'NABÍZÍME', ENGLISH: 'WE OFFER' })}
						</TitleTextCustom>
						<Text mt="3">
							<Link
								to={user ? '/homepage/student' : '/public/student'}
								fontWeight="bold"
								color="secondary"
								css={css`
									text-transform: uppercase;
								`}
							>
								{localize({
									CZECH: 'Služby pro studenty a absolventy',
									ENGLISH: 'Services for students and alumni',
								})}
							</Link>
						</Text>
						<Text color="secondary">
							{localize({
								CZECH:
									'Studentům a absolventům poskytujeme poradenské služby a kariérní poradenství, pomáháme s hledáním ideálních pozic a poskytujeme rady při sestavení životopisu nebo motivačního dopisu.',
								ENGLISH:
									'We provide counselling services and career guidance to students and graduates, help them find the perfect job and offer them advice and tips on how to write a CV or a cover letter.',
							})}
						</Text>
						<Text mt="3">
							<Link
								to={user ? '/homepage/company' : '/public/company'}
								fontWeight="bold"
								color="secondary"
								css={css`
									text-transform: uppercase;
								`}
							>
								{localize({
									CZECH: 'Služby pro firmy',
									ENGLISH: 'Services for companies',
								})}
							</Link>
						</Text>
						<Text mb="3" color="secondary">
							{localize({
								CZECH:
									'Firmám pomáháme s hledáním vhodných uchazečů, zajišťujeme osobní kontakt se studenty a nabízíme cílenou inzerci pracovních nabídek.',
								ENGLISH:
									'We assist companies in finding suitable candidates, we facilitate personal contact with students and offer targeted advertising of job opportunities.',
							})}
						</Text>
					</Box>
					<Flex
						mt={[4, 0]}
						ml={[0, 4]}
						width={[1, 1 / 2]}
						height={400}
						css={css`
							background-image: url(${unicomplex});
							background-size: cover;
							background-repeat: no-repeat;
							background-position: center;
						`}
					/>
				</Flex>

				{/* Video - hide it for now... */}
				{/* <Box
					mt={[4, 0]}
					width={1}
					css={css`
						grid-column-start: 2;
						grid-column-end: 3;
					`}
				>
					<TitleTextCustom color="primary">
						{localize({
							CZECH: 'video jak to funguje',
							ENGLISH: 'video how it works',
						})}
					</TitleTextCustom>
					<Flex
						width={1}
						height={482}
						mt={3}
						bg="secondary"
						justifyContent="center"
						alignItems="center"
					>
						<TitleTextCustom color="primary">VIDEO</TitleTextCustom>
					</Flex>
				</Box> */}
			</ResponsivePadder>

			<Wrapper bg="lightGrey" pb={5} pt={2}>
				<TestimonialCarousel />
			</Wrapper>

			<ResponsivePadder
				ref={studentSectionRef}
				py={5}
				my={3}
				alignItems="center"
				css={(theme: Theme) => css`
					@media (min-width: ${theme.breakpoints[3]}) {
						grid-template-columns: minmax(0, 4fr) minmax(0, 1fr);
						gap: ${theme.space[5]}px;
						display: grid;
					}
				`}
			>
				<Box gridColumn={2} gridRow={1}>
					<Box
						height={[100, null, null, 150]}
						left={[-66, null, null, -33, 60]}
						css={(theme: Theme) => css`
							position: relative;
							top: 0px;
							transform: scale(0.5) rotate(-90deg) translateX(50%)
								translateY(-50%);

							@media (min-width: ${theme.breakpoints[3]}) {
								transform: none;
								top: -140px;
							}
						`}
					>
						<StudentsHorizontal />
					</Box>
				</Box>
				<Flex
					width={1}
					flexDirection={['column', 'row']}
					alignItems={['center', 'flex-start']}
					css={(theme: Theme) => css`
						@media (min-width: 640px) {
							gap: ${theme.space[4]}px;
						}
					`}
				>
					<Flex
						mt={[4, 0]}
						mr={[0, 4]}
						height={400}
						width={[1, 1 / 2]}
						css={css`
							background-image: url(${studentshome});
							background-size: cover;
							background-repeat: no-repeat;
							background-position: center;
						`}
					/>

					<Box width={[1, 1 / 2]} mt={[4, 0]}>
						<TitleTextCustom color="primary">
							{localize({
								CZECH: 'Pro studenty a absolventy',
								ENGLISH: 'For students and alumni',
							})}
						</TitleTextCustom>
						<Text mb={4}>
							{localize({
								CZECH:
									'Potřebujete profesně nasměrovat, pomoct se sestavením životopisu a přípravou na pohovor nebo sehnat práci či stáž? Se vším pomůžeme v Job Centru UTB. Pro studenty a absolventy, do 2 let po ukončení studia, poskytujeme poradenství zdarma.',
								ENGLISH:
									'Do you need career counselling, seek help with writing a CV, advice on how to prepare for a job interview or how to find a job or an internship? We will provide you with comprehensive assistance services in TBU Job Centre. Counselling services are provided free of charge to students and recent graduates (no more than 2 years after completion of studies).',
							})}
						</Text>
						<TitleTextCustom color="primary">
							{localize({
								CZECH: 'Job Portal',
								ENGLISH: 'Job Portal',
							})}
						</TitleTextCustom>
						<Text>
							{localize({
								CZECH:
									'Job Portal propojuje studenty a zaměstnavatele. Nahrajte svůj životopis, osobní informace a dostávejte pracovní nabídky na míru.',
								ENGLISH:
									'The Job Portal interconnects students and employers. Upload your Curriculum Vitae and personal information to receive made-to-measure job offers.',
							})}
						</Text>
						{user ? null : (
							<NavLinkButton variant="primary" mt={4} px={4} to="/login">
								{localize({
									CZECH: 'Registrace Job Portal',
									ENGLISH: 'Job Portal registration',
								})}
							</NavLinkButton>
						)}
					</Box>
				</Flex>
			</ResponsivePadder>

			<Wrapper bg="primary" py={5} ref={eventsSectionRef}>
				<ResponsivePadder my={[3, 4]}>
					<Grid
						css={(theme: Theme) => css`
							gap: ${theme.space[3]}px;
							color: black;

							@media (min-width: 640px) {
								grid-template-columns: 1fr 1fr 1fr;
								gap: ${theme.space[5]}px;
							}
						`}
					>
						{homepageOffers.map((o, i) => (
							<Fragment key={i}>{HomepageCard(o)}</Fragment>
						))}
					</Grid>
				</ResponsivePadder>
			</Wrapper>

			<ResponsivePadder
				py={5}
				my={3}
				alignItems="center"
				css={(theme: Theme) => css`
					color: black;
					@media (min-width: ${theme.breakpoints[3]}) {
						grid-template-columns: minmax(0, 1fr) minmax(0, 4fr);
						gap: ${theme.space[5]}px;
						display: grid;
					}
				`}
				ref={companySectionRef}
			>
				<Box gridColumn={1} gridRow={1}>
					<Box
						height={[100, null, null, 150]}
						left={[46, null, null, 23, 0]}
						css={(theme: Theme) => css`
							position: relative;
							top: 0px;
							transform: scale(0.5) rotate(90deg) translateX(-50%)
								translateY(-50%);

							@media (min-width: ${theme.breakpoints[3]}) {
								transform: none;
								top: -120px;
							}
						`}
					>
						{i18n.language === 'en' ? (
							<CompanyEnHorizontal />
						) : (
							<CompanyHorizontal />
						)}
					</Box>
				</Box>
				<Flex
					width={1}
					flexDirection={['column', 'row']}
					alignItems={['center', 'flex-start']}
					css={(theme: Theme) => css`
						@media (min-width: 640px) {
							gap: ${theme.space[4]}px;
						}
					`}
				>
					<Box width={[1, 1 / 2]} mt={[4, 0]}>
						<TitleTextCustom color="primary">
							{localize({
								CZECH: 'Pro firmy a zaměstnavatele',
								ENGLISH: 'For companies and employers',
							})}
						</TitleTextCustom>
						<Text mb={4} color="secondary">
							{localize({
								CZECH:
									'Nabízíme Vám služby, které Vás zviditelní na půdě univerzity, pomáháme s náborem, zajišťujeme osobní kontakt se studenty a nabízíme inzerci Vašich pracovních nabídek. Jsme Vaše spojka mezi Vámi a studenty.',
								ENGLISH:
									'We offer services that will give you greater visibility among the academic community; we help with recruitment, facilitate personal contact with students and offer advertising of your job vacancies. We facilitate interconnection between you and our students.',
							})}
						</Text>
						<TitleTextCustom color="primary">
							{localize({
								CZECH: 'Job Portal',
								ENGLISH: 'Registration with the Job Portal',
							})}
						</TitleTextCustom>
						<Text color="secondary">
							{localize({
								CZECH:
									'Inzerujte své pracovní nabídky a vyhledávejte vhodné zaměstnance pomocí našeho Job Portal.',
								ENGLISH:
									'Advertise your job vacancies and find suitable candidates via our Job Portal.',
							})}
						</Text>
						{user ? null : (
							<NavLinkButton
								variant="primary"
								mt={4}
								px={4}
								to="/registration/company"
							>
								{localize({
									CZECH: 'Registrace Job Portal',
									ENGLISH: 'Job Portal registration',
								})}
							</NavLinkButton>
						)}
					</Box>

					<Flex
						mt={[4, 0]}
						mr={[0, 4]}
						height={400}
						width={[1, 1 / 2]}
						css={css`
							background-image: url(${companyhome});
							background-size: cover;
							background-repeat: no-repeat;
							background-position: calc(50% - 32px) center;
						`}
					/>
				</Flex>
			</ResponsivePadder>
			<Wrapper bg="lightGrey" py={5}>
				<ResponsivePadder
					my={[3, 4]}
					css={(theme: Theme) => css`
						display: grid;
						gap: ${theme.space[4]}px;
						color: black;

						@media (min-width: 640px) {
							grid-template-columns: 1fr 1fr 1fr;
							gap: ${theme.space[5]}px;
						}
					`}
				>
					{homepageEvents.map((o, i) => (
						<Fragment key={i}>{HomepageCard(o)}</Fragment>
					))}
				</ResponsivePadder>
			</Wrapper>

			<Wrapper
				bg="primary"
				color="white"
				pt={5}
				pb={[0, null, null, null, 4]}
				ref={contactSectionRef}
			>
				<ResponsivePadder
					py={5}
					alignItems="center"
					css={(theme: Theme) => css`
						color: white;
						@media (min-width: ${theme.breakpoints[3]}) {
							grid-template-columns: minmax(0, 4fr) minmax(0, 1fr);
							gap: ${theme.space[5]}px;
							display: grid;
						}
					`}
				>
					<Box gridColumn={2} gridRow={1}>
						<Box
							css={(theme: Theme) => css`
								transform: translateX(-100%) translateY(-100%) scale(0.6)
									rotate(-90deg);
								height: 50px;
								position: relative;
								left: -40px;
								@media (min-width: ${theme.breakpoints[3]}) {
									transform: none;
									height: auto;
									left: 60px;
								}
							`}
						>
							{i18n.language === 'en' ? (
								<ContactEnHorizontal />
							) : (
								<ContactHorizontal />
							)}
						</Box>
					</Box>
					<Wrapper
						pb={4}
						width="100%"
						color="white"
						css={(theme: Theme) => css`
							display: block;
							@media (min-width: ${theme.breakpoints[3]}) {
								display: none;
							}
						`}
					>
						<ResponsivePadder
							justifyContent={['center', 'space-around']}
							width={1}
							alignItems="center"
							flexDirection={['column', 'row']}
						>
							<Box as="span">
								<PhoneIcon fill="white" />
								<HrefLink color="white" ml={3} href="tel:+420731137873">
									+420 731 137 873
								</HrefLink>
							</Box>
							<Box as="span" mt={[4, 0]}>
								<MailIcon fill="white" />
								<HrefLink color="white" ml={3} href="mailto:jobcentrum@utb.cz">
									jobcentrum@utb.cz
								</HrefLink>
							</Box>

							<Box as="span" mt={[4, 0]}>
								<ClockIcon fill="white" />
								<Text as="span" color="white" ml={3}>
									{localize({
										CZECH: 'Po',
										ENGLISH: 'Mon',
									})}
									&thinsp;-&thinsp;
									{localize({
										CZECH: 'Pá',
										ENGLISH: 'Fri',
									})}
									&ensp;9:00&thinsp;-&thinsp;15:00
								</Text>
							</Box>
						</ResponsivePadder>
					</Wrapper>
					<Flex
						width={1}
						flexDirection={['column', 'row']}
						alignItems={['center', 'flex-start']}
					>
						<Grid
							css={(theme: Theme) => css`
								gap: ${theme.space[4]}px;
								color: black;

								@media (min-width: ${theme.breakpoints[1]}) {
									grid-template-columns: 1fr 1fr 1fr;
									gap: ${theme.space[5]}px;
								}
							`}
						>
							{contactPersons.map((p, i) => (
								<Fragment key={i}>{ContactCard(p)}</Fragment>
							))}
						</Grid>
					</Flex>
					<Box>
						<Text
							as="h3"
							color="white"
							mt={[4, null, null, null, 0]}
							mb={0}
							css={(theme: Theme) => css`
								text-transform: none;
								font-size: 2rem;
								@media (min-width: ${theme.breakpoints[1]}) {
									font-size: 3rem;
								}
							`}
						>
							{localize({
								CZECH: 'Konzultační hodiny',
								ENGLISH: 'Consultation hours',
							})}
						</Text>
						<Box gridColumn={1}>
							<Flex width={1} alignItems="center">
								<Text
									css={css`
										font-size: 1.25rem;
									`}
								>
									{localize({
										CZECH: 'Po',
										ENGLISH: 'Mon',
									})}{' '}
									-{' '}
									{localize({
										CZECH: 'Pá',
										ENGLISH: 'Fri',
									})}{' '}
									&emsp; 09:00 - 15:00
								</Text>
								<HrefLink
									href="https://www.facebook.com/www.jobcentrum.utb.cz"
									target="_blank"
									ml={4}
								>
									<Flex alignItems="center" color="white">
										<FaFacebookF size={22} />
									</Flex>
								</HrefLink>
								<HrefLink
									href="https://www.instagram.com/jobcentrumutb/"
									target="_blank"
									ml={4}
								>
									<Flex alignItems="center" color="white">
										<FaInstagram size={22} />
									</Flex>
								</HrefLink>
							</Flex>
						</Box>
					</Box>
				</ResponsivePadder>
			</Wrapper>

			<Wrapper>
				{/* 	<iframe
					height="500"
					id="gmap_canvas"
					src="https://maps.google.com/maps?q=n%C3%A1m.%20T.%20G.%20Masaryka%205555,%20760%2001%20Zl%C3%ADn&t=&z=17&ie=UTF8&iwloc=&output=embed"
					frameBorder="0"
					scrolling="no"
				/> */}
				<Flex
					css={css`
						background-image: url(${mapPlaceholder});
						background-size: cover;
						background-repeat: no-repeat;
						background-position: center;
						height: 500px;
					`}
				/>
			</Wrapper>

			<ScrollToTopButton />
		</Wrapper>
	);
};

export default ZlinHomepage;
